import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import {
  deepSetObject, generateRedirectUrl, launchModal,
} from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import { loginAsEntityUser, mergeAndStringifyEntityObjects } from '@functions/entity'

import * as userActions from '@redux/modules/user'
import * as currentUserActions from '@redux/modules/currentUser'

import defaultFormState, { requiredFields } from '@models/user'

import PageContext from '@contexts/pageContext'

import useCurrentUser from '@hooks/useCurrentUser'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'
import type { AgencyModel, UserModel, UserRequestOptions } from '@models/types'

export const generateUrls = (user?: Partial<UserModel>, options: { prefixRoute?: string } = {}) => {
  const { id } = user || {}
  const { prefixRoute = 'systemManager' } = options

  return {
    editUserDetailsUrl: `#/${prefixRoute}/users/${id}/edit/details`,
    editUserUrl: `#/${prefixRoute}/users/${id}/edit/`,
    usersIndexUrl: `#/${prefixRoute}/users`,
  }
}

type CreateUserParams = {
  dispatch: AppDispatch,
  prefixRoute?: string,
  requestOptions?: UserRequestOptions,
  userParams: Partial<UserModel>,
}

const createUser = (params: CreateUserParams) => {
  const {
    dispatch, prefixRoute, requestOptions, userParams,
  } = params
  const { createUser: createFn } = userActions

  const stringifyObjectKeys: (keyof UserModel)[] = ['custom_permissions', 'data', 'options']
  const updatedParams = mergeAndStringifyEntityObjects({}, userParams, stringifyObjectKeys)

  return dispatch(createFn(updatedParams, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response
      const { id } = data

      const { editUserUrl } = generateUrls({ id }, { prefixRoute })

      return {
        data,
        redirectUrl: `${editUserUrl}?redirect=${generateRedirectUrl()}`,
        success,
      }
    }

    return response
  })
}

type UpdateUserParams = {
  dispatch: AppDispatch,
  isSelf?: boolean,
  requestOptions?: UserRequestOptions,
  user: UserModel,
  userParams: Partial<UserModel>,
}

const updateUser = (params: UpdateUserParams) => {
  const {
    dispatch, isSelf, requestOptions, user, userParams,
  } = params

  const { updateUser: updateFn } = userActions
  const { hydrateCurrentUser: hydrateFn } = currentUserActions

  const stringifyObjectKeys: (keyof UserModel)[] = ['custom_permissions', 'data', 'options']
  const updatedParams = mergeAndStringifyEntityObjects(user, userParams, stringifyObjectKeys)

  return dispatch(updateFn(updatedParams, requestOptions)).then((response) => {
    const { success } = response

    if (success && isSelf){
      dispatch(hydrateFn(response?.data))
    }

    return response
  })
}

type DeleteUserParams = {
  dispatch: AppDispatch,
  user: DeleteParams<UserModel>,
}

const deleteUser = (params: DeleteUserParams) => {
  const { dispatch, user } = params
  const { deleteUser: deleteFn } = userActions

  return dispatch(deleteFn(user))
}

type SoftDeleteUserParams = {
  dispatch: AppDispatch,
  user: UserModel,
}

const softDeleteUser = (params: SoftDeleteUserParams) => {
  const { dispatch, user } = params
  const { softDeleteUser: softDeleteFn } = userActions
  const userParams = { deleted: true }
  const updatedParams = mergeAndStringifyEntityObjects(user, userParams)

  return dispatch(softDeleteFn(updatedParams))
}

type UnDeleteUserParams = {
  dispatch: AppDispatch,
  user: UserModel,
}

const undeleteUser = (params: UnDeleteUserParams) => {
  const { dispatch, user } = params
  const { undeleteUser: undeleteFn } = userActions
  const userParams = { deleted: false }
  const updatedParams = mergeAndStringifyEntityObjects(user, userParams)

  return dispatch(undeleteFn(updatedParams))
}

type ExportUserPrams = {
  dispatch: AppDispatch,
  user: DeleteParams<UserModel>,
}

export const exportUser = (params: ExportUserPrams) => {
  const { user, dispatch } = params
  const { exportUser: exportFn } = userActions

  return dispatch(exportFn(user))
}

type TransferCampaignsParams = {
  dispatch: AppDispatch,
  transferParams: { newAgentId: number },
  user: DeleteParams<UserModel>,
}

const transferCampaigns = (params: TransferCampaignsParams) => {
  const {
    user,
    transferParams: { newAgentId },
    dispatch,
  } = params
  const { transferCampaigns: transferFn } = userActions

  return dispatch(transferFn(user, newAgentId))
}

type TransferCampaignsAndDeleteUserParams = {
  agency: AgencyModel,
  dispatch: AppDispatch,
  transferParams: { newAgentId: number },
  user: DeleteParams<UserModel>,
}

const transferCampaignsAndDeleteUser = async (params: TransferCampaignsAndDeleteUserParams) => {
  const { agency, user, dispatch } = params

  const transferResponse = await transferCampaigns(params)
  const { success: transferSuccess } = transferResponse || {}

  if (transferSuccess){
    return deleteUser({ user, dispatch }).then((response) => {
      const { success } = response

      if (success){
        const { usersIndexUrl } = generateUrls({}, { prefixRoute: `agencies/${agency.id}/manage` })

        return {
          redirectUrl: usersIndexUrl,
          success,
        }
      }

      return response
    })
  }

  return transferResponse
}

type UpdateTwoFactorMethod = {
  dispatch: AppDispatch,
  requestOptions: UserRequestOptions,
  user: DeleteParams<UserModel>,
}

const updateTwoFactorMethod = (params: UpdateTwoFactorMethod) => {
  const { dispatch, requestOptions, user } = params
  const { updateTwoFactorMethod: updateFn } = userActions

  return dispatch(updateFn(user, requestOptions))
}

type GenerateUserAuthenticationTokenParams = {
  dispatch: AppDispatch,
  requestOptions?: UserRequestOptions,
  user: DeleteParams<UserModel>,
}

const generateUserAuthenticationToken = (params: GenerateUserAuthenticationTokenParams) => {
  const { dispatch, requestOptions, user } = params

  const { generateUserAuthenticationToken: generateFn } = userActions

  const generateAuthTokenRequestOptions = {
    ...requestOptions,
    include_user_authentication_token: true,
  }

  return dispatch(generateFn(user, generateAuthTokenRequestOptions))
}

type ImportUsersCsvParams = {
  userParams: {
    fileData: object,
    email: string,
  },
  dispatch: AppDispatch,
}

const importUsersCsv = (params: ImportUsersCsvParams) => {
  const {
    userParams: { fileData, email },
    dispatch,
  } = params

  const { importUsersCsv: importFn } = userActions
  // @ts-expect-error TS2345
  return dispatch(importFn(fileData, email))
}

type LoginAsUserParams = {
  dispatch: AppDispatch,
  requestOptions: UserRequestOptions,
  user: UserModel,
}
const loginAsUser = (params: LoginAsUserParams) => {
  const { user, dispatch, requestOptions } = params

  return dispatch(loginAsEntityUser('users', user.id, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response

      return {
        success,
        // @ts-expect-error TS2339
        redirectUrl: data.url,
      }
    }

    return response
  })
}

type SaveUserDashboardParams = {
  activeLayout: object,
  dashboardKey: string,
  dispatch: AppDispatch,
  requestOptions?: UserRequestOptions,
  user: UserModel,
}

const saveUserDashboard = (params: SaveUserDashboardParams) => {
  const {
    dashboardKey, activeLayout, user, dispatch, requestOptions,
  } = params

  // @ts-expect-error TS2345
  const updatedUser = deepSetObject({ ...user }, `options.custom_dashboards.${dashboardKey}`, activeLayout)

  return updateUser({
    user,
    // @ts-expect-error TS2322
    userParams: updatedUser,
    dispatch,
    requestOptions,
  })
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useUserForm(
  user: Partial<UserModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const userForm = useForm(
    defaultFormState,
    { entity: user, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [user.id, user.cache_key],
  )

  return {
    ...userForm,
  }
}

type UnblockUserParams = {
  user: UserModel,
  dispatch: AppDispatch,
}

const unblockUser = (params: UnblockUserParams) => {
  const { user, dispatch } = params
  const { unblockUser: unblockFn } = userActions

  return dispatch(unblockFn(user))
}

export const useRelations = (user: Partial<UserModel> = {}) => {
  const {
    association_id, association_type, image_id, role_id,
  } = user

  const {
    agencies, brands, images, providers, roles,
  } = useSelector(reduxState => reduxState.entities)

  const agency = association_type === 'Agency' && association_id ? agencies[association_id] || {} : {}
  const brand = association_type === 'Brand' && association_id ? brands[association_id] || {} : {}
  const image = image_id ? images[image_id] || {} : {}
  const provider = association_type === 'Provider' && association_id ? providers[association_id] || {} : {}
  const role = role_id ? roles[role_id] || {} : {}

  return {
    agency,
    brand,
    image,
    provider,
    role,
  }
}

type Options = {
  generateUrlsOptions?: {
    prefixRoute?: string,
  },
  performHttpRequests?: boolean,
  requestOptions?: UserRequestOptions,
}

function useUser(initEntity: Partial<UserModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options
  const { generateUrlsOptions } = options
  const { prefixRoute } = generateUrlsOptions || {}

  // @ts-expect-error TS2741
  const { entity: user }: { user: UserModel } = useLatestEntity(initEntity, 'users')
  const { id } = user

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const {
    agency, brand, image, provider, role,
  } = useRelations(user)

  useReduxAction(
    'users',
    'loadUser',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.users)

  const supportUserIds = user?.data?.support_team?.user_ids || []
  const realbaseId = user?.data?.external_ids?.realbase || ''

  const currentUserPayload = useCurrentUser()
  const { currentUser } = currentUserPayload
  const isSelf = user ? currentUser.id === user.id : false

  const quickLinks = user?.options?.quick_links || []
  const hasQuickLinks = !!quickLinks.length

  return {
    agency,
    brand,
    callbacks: {
      addUser: (customPayload: object = {}) => launchModal({
        callbacks,
        modalKey: 'CreateUserModal',
        payload: { user, ...customPayload },
      }),
      createUser: (userParams: Partial<UserModel>, entityOptions?: UserRequestOptions) => (
        createUser({
          userParams, dispatch, prefixRoute, requestOptions: entityOptions,
        })
      ),
      deleteAgent: () => launchModal({
        callbacks,
        modalKey: 'DeleteAgentModal',
        payload: { user },
      }),
      deleteUser: () => deleteUser({ dispatch, user }),
      undeleteUser: () => undeleteUser({ dispatch, user }),
      softDeleteUser: () => softDeleteUser({ dispatch, user }),
      exportUser: () => exportUser({ user, dispatch }),
      generateReport: (customPayload: object = {}) => launchModal({
        callbacks,
        modalKey: 'GenerateReportModal',
        payload: { ...customPayload },
      }),
      generateUserAuthenticationToken:
        (_: unknown, entityOptions?: UserRequestOptions) => generateUserAuthenticationToken({
          user,
          dispatch,
          requestOptions: entityOptions,
        }),
      importUsersCsv: (userParams: {
        fileData: object,
        email: string,
      }) => importUsersCsv({ userParams, dispatch }),
      createOrEditCustomLinks: (customPayload: {}) => launchModal({
        callbacks,
        modalKey: 'ManageEntityQuickLinksModal',
        payload: { ...customPayload, entity: user, entityKey: 'users' },
      }),
      loginAsUser: (entityOptions: UserRequestOptions) => loginAsUser(
        { user, dispatch, requestOptions: entityOptions },
      ),
      manageAuthToken: () => launchModal({
        callbacks,
        modalKey: 'GenerateAuthTokenModal',
        payload: { user },
      }),
      manageEbuAssignedAgentsModal: () => launchModal({
        callbacks,
        modalKey: 'ManageEbuAssignedAgentsModal',
        payload: { user },
      }),
      manageNotifications: () => launchModal({
        callbacks,
        modalKey: 'ManageNotificationOptionsModal',
        payload: { user },
      }),
      manageUserApplications: () => launchModal({
        callbacks,
        modalKey: 'ManageUserApplicationsModal',
        payload: { user },
      }),
      manageUserPermissions: () => launchModal({
        callbacks,
        modalKey: 'ManageUserPermissionsModal',
        payload: { user },
      }),
      manageUserSupportTeam: () => launchModal({
        callbacks,
        modalKey: 'ManageEntitySupportTeamModal',
        payload: {
          callbacks: {
            updateEntity: (userParams: Partial<UserModel>, entityOptions?: UserRequestOptions) => updateUser({
              user,
              userParams,
              dispatch,
              requestOptions: entityOptions,
            }),
          },
          entity: user,
        },
      }),
      manageUserTeamMemberRoles: () => launchModal({
        callbacks,
        modalKey: 'ManageUserTeamMemberRolesModal',
        payload: { user },
      }),
      manage2FASettings: () => launchModal({
        callbacks,
        modalKey: 'Manage2FASettingsModal',
        payload: { user },
      }),
      saveUserDashboard: (
        dashboardKey: string,
        activeLayout: object,
        entityOptions?: UserRequestOptions,
      ) => saveUserDashboard({
        dashboardKey,
        activeLayout,
        user,
        dispatch,
        requestOptions: entityOptions,
      }),
      transferCampaigns: (
        transferParams: { newAgentId: number },
      ) => transferCampaigns({ user, transferParams, dispatch }),
      transferCampaignsAndDeleteUser: (transferParams: { newAgentId: number }) => transferCampaignsAndDeleteUser({
        // @ts-expect-error TS2740
        agency,
        user,
        transferParams,
        dispatch,
      }),
      unblockUser: () => unblockUser({ user, dispatch }),
      updateTwoFactorMethod: (
        _: unknown,
        entityOptions: UserRequestOptions,
      ) => updateTwoFactorMethod({ user, dispatch, requestOptions: entityOptions }),
      updateUser: (userParams: Partial<UserModel>, entityOptions?: UserRequestOptions) => (
        updateUser({
          user, userParams, dispatch, requestOptions: entityOptions, isSelf,
        })
      ),
    },
    creating,
    currentUserPayload,
    deleting,
    hasQuickLinks,
    image,
    isAgent: user.actable_type === 'Agent',
    isDeleted: user.deleted === true,
    isOfficeAdministrator: user.actable_type === 'OfficeAdministrator',
    isSelf,
    loading: loading && !loadedIds?.includes(user.id),
    provider,
    quickLinks,
    realbaseId,
    role,
    supportUserIds,
    updating,
    user,
  }
}

export type UseUserPayload = ReturnType<typeof useUser>

export default useUser
